import { useEffect, useMemo, useState, useRef } from 'react';
import {
  collection, doc, onSnapshot, orderBy, query, where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';
import {
  ymdInTimezone,
  getStatusForDate,
  entryCoversDate,
  describeEntry,
  toJsDate,
} from '../../lib/availability-helpers.js';

// CAL-V2 2026-05-17: Calendar-first availability UX.
//
// The calendar IS the source of truth. Click (or drag) days, pick a status + an
// "apply to" rule (just these days / every weekday / every other weekday), save.
// Duplicates are prevented at create time (exact-match guard) and there's an
// "auto-clean redundant entries" button for the existing pile.
//
// Month-by-month navigation lets responders plan up to ~12 months ahead.
// Default view: the current calendar month.
//
// The list of raw entries is moved to a collapsible "Advanced" panel — the
// average responder never needs to see it.

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function Availability() {
  const { user, claims } = useAuth();
  const vendorId = claims?.vendor_id;
  const myUid = user?.uid;

  const [vendor, setVendor] = useState(null);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);
  const [loadingEntries, setLoadingEntries] = useState(true);

  // Selection state for the click/drag interaction. Set of YYYY-MM-DD strings.
  const [selection, setSelection] = useState(new Set());
  const [dragging, setDragging] = useState(false);

  // Popover (the "apply status to selected days" modal).
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Advanced / power-user panel state.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedAdvIds, setSelectedAdvIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Sick / cover-request modal (existing).
  const [sickModalOpen, setSickModalOpen] = useState(false);

  // Existing rule-form modal for the rare case a user wants the full form.
  const [ruleFormOpen, setRuleFormOpen] = useState(false);
  const [ruleFormSeed, setRuleFormSeed] = useState(null);

  // Cleanup toast.
  const [cleanupMsg, setCleanupMsg] = useState(null);

  // ---- Live subscriptions ---------------------------------------------------

  useEffect(() => {
    if (!vendorId) return;
    const unsub = onSnapshot(
      doc(db, 'vendors', vendorId),
      (snap) => setVendor(snap.exists() ? snap.data() : {}),
      (err) => setError(err.message),
    );
    return () => unsub();
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId || !myUid) return;
    const q = query(
      collection(db, 'vendors', vendorId, 'availability'),
      where('responderUid', '==', myUid),
      orderBy('startDate', 'asc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingEntries(false);
      },
      (err) => {
        setError(err.message);
        setLoadingEntries(false);
      },
    );
    return () => unsub();
  }, [vendorId, myUid]);

  // ---- Derived ----

  const tz = vendor?.timezone || '';
  const availabilityModel = vendor?.availabilityModel || 'opt-out';

  // Today's YMD in vendor TZ — used to hide past rows and disable past cells.
  // CAL-FUTURE 2026-05-17: responders only see today onwards.
  const todayYmd = useMemo(() => ymdInTimezone(new Date(), tz), [tz]);

  // CAL-WINDOW 2026-05-20: rolling window instead of month paging. Responders see
  // a fixed span — 4 days back (read-only context) through 28 days forward (the
  // coverage horizon). No month navigation; the window is always anchored to today.
  const WINDOW_DAYS_BACK = 4;
  const WINDOW_DAYS_FORWARD = 28;

  const { visibleRows, windowLabel } = useMemo(() => {
    const anchor = new Date();
    const today0 = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    const winStart = new Date(today0); winStart.setDate(today0.getDate() - WINDOW_DAYS_BACK);
    const winEnd = new Date(today0); winEnd.setDate(today0.getDate() + WINDOW_DAYS_FORWARD);
    const winStartYmd = ymdInTimezone(winStart, tz);
    const winEndYmd = ymdInTimezone(winEnd, tz);

    // Pad to whole weeks (Sun..Sat) so the 7-column grid stays aligned.
    const gridStart = new Date(winStart); gridStart.setDate(winStart.getDate() - winStart.getDay());
    const gridEnd = new Date(winEnd); gridEnd.setDate(winEnd.getDate() + (6 - winEnd.getDay()));
    const totalDays = Math.round((gridEnd - gridStart) / 86400000) + 1;

    const cells = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      const ymd = ymdInTimezone(d, tz);
      const status = getStatusForDate(entries, ymd, tz);
      const implicitlyAvailable = status === 'off' && availabilityModel === 'opt-out';
      const inWindow = ymd >= winStartYmd && ymd <= winEndYmd;
      const isPast = ymd < todayYmd;
      // Editable = today .. +28 only. The 4 back days (past) and any week-filler
      // beyond +28 are visible but non-interactive.
      const disabled = isPast || ymd > winEndYmd;
      // inMonth is reused by BigCalendarCell for dimming: true = inside the window.
      cells.push({ date: d, ymd, status, implicitlyAvailable, inMonth: inWindow, isPast, disabled });
    }

    const rows = [];
    for (let r = 0; r < cells.length; r += 7) rows.push(cells.slice(r, r + 7));

    const fmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
    return { visibleRows: rows, windowLabel: `${fmt.format(winStart)} – ${fmt.format(winEnd)}` };
  }, [entries, tz, availabilityModel, todayYmd]);

  // ---- Click + drag selection ----

  function onPointerDownCell(ymd) {
    setSelection(new Set([ymd]));
    setDragging(true);
  }
  function onPointerEnterCell(ymd) {
    if (!dragging) return;
    setSelection((prev) => {
      if (prev.has(ymd)) return prev;
      const next = new Set(prev);
      next.add(ymd);
      return next;
    });
  }
  function onPointerUpAnywhere() {
    if (dragging && selection.size > 0) {
      setPopoverOpen(true);
    }
    setDragging(false);
  }
  // Bind pointerup at the document level so releases outside the grid still close the drag.
  useEffect(() => {
    if (!dragging) return;
    const handler = () => onPointerUpAnywhere();
    window.addEventListener('pointerup', handler);
    return () => window.removeEventListener('pointerup', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, selection.size]);

  // ---- Save the popover (creates one or more entries) ----

  async function saveStatusForSelection({ status, applyTo, reason }) {
    if (selection.size === 0) return;
    setError(null);

    const sortedYmds = [...selection].sort();
    const reasonClean = (reason || '').trim() || null;

    try {
      if (applyTo === 'just-these') {
        // CAL-LAST-WINS 2026-05-17: wipe any existing one-off entries that overlap
        // any of the selected days. Recurring rules are NOT touched — the new
        // one-off acts as an override for those specific days only.
        const selectedSet = new Set(sortedYmds);
        const toWipe = [];
        for (const e of entries) {
          if (e.type !== 'one-off') continue;
          for (const ymd of selectedSet) {
            if (entryCoversDate(e, ymd, tz)) {
              toWipe.push(e.id);
              break;
            }
          }
        }
        if (toWipe.length > 0) {
          await Promise.all(
            toWipe.map((id) => httpsCallable(functions, 'removeAvailability')({ id })),
          );
        }

        // Now add the new entry/entries. Group contiguous days into ranges.
        const groups = groupContiguous(sortedYmds);
        for (const group of groups) {
          const startYmd = group[0];
          const endYmd = group[group.length - 1];
          await httpsCallable(functions, 'addAvailability')({
            entry: {
              status,
              type: 'one-off',
              startDate: ymdToMidnightISO(startYmd),
              endDate: group.length > 1 ? ymdToMidnightISO(endYmd) : null,
              daysOfWeek: null,
              weekInterval: null,
              reason: reasonClean,
            },
          });
        }
      } else if (applyTo === 'weekly' || applyTo === 'every-other') {
        // Use the first selected day's weekday as the rule's weekday. Start date is
        // the first selected day. No end date (open-ended). Power users can edit
        // via Advanced later if they want to bound it.
        const firstDate = ymdToDate(sortedYmds[0]);
        const dow = firstDate.getDay();

        // CAL-RECUR-WIPE (Task #95, 2026-05-20): wipe any existing OPEN-ENDED
        // recurring rules that cover this weekday before adding the new one.
        // Without this, toggling "every other Fri Available" then "every other
        // Fri Unavailable" via the popover stacked BOTH rules, and the resolver
        // would have to tie-break between them. Recurring rules with an
        // endDate set are LEFT ALONE — those are bounded patterns the user
        // explicitly limited via Advanced; new popover clicks shouldn't nuke
        // them.
        const toWipe = entries.filter((e) => {
          if (e.type !== 'weekly' && e.type !== 'every-n-weeks') return false;
          if (e.endDate) return false; // bounded — preserve
          const dows = Array.isArray(e.daysOfWeek) ? e.daysOfWeek : [];
          return dows.includes(dow);
        });
        if (toWipe.length > 0) {
          await Promise.all(
            toWipe.map((e) => httpsCallable(functions, 'removeAvailability')({ id: e.id })),
          );
        }

        await httpsCallable(functions, 'addAvailability')({
          entry: {
            status,
            type: applyTo === 'weekly' ? 'weekly' : 'every-n-weeks',
            startDate: ymdToMidnightISO(sortedYmds[0]),
            endDate: null,
            daysOfWeek: [dow],
            weekInterval: applyTo === 'every-other' ? 2 : null,
            reason: reasonClean,
          },
        });
      }

      setPopoverOpen(false);
      setSelection(new Set());
    } catch (err) {
      setError(err.message || String(err));
    }
  }

  // ---- Clean up duplicates ----
  // Heuristic: among one-off entries, group by (status, startYmd, endYmd, reason).
  // For each group with >1, keep one, delete the rest. Then a second pass:
  // remove one-off entries whose range is fully contained inside a longer
  // entry of the same status.

  async function cleanDuplicates() {
    setError(null);
    setCleanupMsg(null);

    const oneOffs = entries.filter((e) => e.type === 'one-off');
    const toDelete = new Set();

    // Pass 1: exact duplicates
    const byKey = new Map();
    for (const e of oneOffs) {
      const key = [
        e.status,
        ymdInTimezone(toJsDate(e.startDate), tz),
        e.endDate ? ymdInTimezone(toJsDate(e.endDate), tz) : '',
        (e.reason || '').trim(),
      ].join('|');
      const list = byKey.get(key) || [];
      list.push(e);
      byKey.set(key, list);
    }
    for (const list of byKey.values()) {
      if (list.length > 1) {
        // keep first, delete the rest
        for (let i = 1; i < list.length; i++) toDelete.add(list[i].id);
      }
    }

    // Pass 2: fully contained ranges (same status)
    const surviving = oneOffs.filter((e) => !toDelete.has(e.id));
    for (const e of surviving) {
      const eStart = ymdInTimezone(toJsDate(e.startDate), tz);
      const eEnd = e.endDate ? ymdInTimezone(toJsDate(e.endDate), tz) : eStart;
      for (const other of surviving) {
        if (other.id === e.id) continue;
        if (other.status !== e.status) continue;
        const oStart = ymdInTimezone(toJsDate(other.startDate), tz);
        const oEnd = other.endDate ? ymdInTimezone(toJsDate(other.endDate), tz) : oStart;
        // If "other" fully covers "e" (and other is strictly larger or same), delete e
        if (oStart <= eStart && oEnd >= eEnd && (oEnd > eEnd || oStart < eStart)) {
          toDelete.add(e.id);
          break;
        }
      }
    }

    // Pass 3 (CAL-SHADOW 2026-05-17): fully-shadowed-by-more-recent.
    // With the last-wins resolver, an entry is pure noise if every date it
    // covers has at least one MORE RECENT one-off entry also covering that
    // date. The newer entry always wins, so the older one contributes nothing
    // to the visible calendar. Handles the "Available + Unavailable on the
    // same date" stacks the user has been seeing.
    const stillSurviving = oneOffs.filter((e) => !toDelete.has(e.id));
    for (const e of stillSurviving) {
      const eDates = datesForOneOffEntry(e, tz);
      const eTime = createdAtMs(e);
      if (eDates.length === 0) continue;
      const fullyShadowed = eDates.every((ymd) =>
        stillSurviving.some((other) =>
          other.id !== e.id &&
          createdAtMs(other) > eTime &&
          entryCoversDate(other, ymd, tz),
        ),
      );
      if (fullyShadowed) toDelete.add(e.id);
    }

    if (toDelete.size === 0) {
      setCleanupMsg('No redundant entries found.');
      setTimeout(() => setCleanupMsg(null), 4000);
      return;
    }

    if (!confirm(`Found ${toDelete.size} redundant entries. Delete them? Recurring rules and unique date ranges are preserved.`)) return;

    setBulkBusy(true);
    const results = await Promise.allSettled(
      [...toDelete].map((id) => httpsCallable(functions, 'removeAvailability')({ id })),
    );
    setBulkBusy(false);
    const failed = results.filter((r) => r.status === 'rejected').length;
    setCleanupMsg(`Cleaned up ${toDelete.size - failed} redundant entr${toDelete.size - failed === 1 ? 'y' : 'ies'}.${failed ? ` (${failed} failed.)` : ''}`);
    setTimeout(() => setCleanupMsg(null), 6000);
  }

  // ---- Advanced section actions ----

  async function removeEntry(id) {
    if (!confirm('Delete this availability entry?')) return;
    setError(null);
    try {
      await httpsCallable(functions, 'removeAvailability')({ id });
      setSelectedAdvIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      setError(err.message || String(err));
    }
  }
  function toggleAdvSelected(id) {
    setSelectedAdvIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function selectAllAdv() { setSelectedAdvIds(new Set(entries.map((e) => e.id))); }
  function clearAdvSelection() { setSelectedAdvIds(new Set()); }
  async function deleteSelectedAdv() {
    if (selectedAdvIds.size === 0) return;
    if (!confirm(`Delete ${selectedAdvIds.size} entr${selectedAdvIds.size === 1 ? 'y' : 'ies'}?`)) return;
    setError(null);
    setBulkBusy(true);
    const results = await Promise.allSettled(
      [...selectedAdvIds].map((id) => httpsCallable(functions, 'removeAvailability')({ id })),
    );
    setBulkBusy(false);
    setSelectedAdvIds(new Set());
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed) setError(`${failed} of ${results.length} deletes failed.`);
  }
  function openRuleFormForEdit(entry) {
    setRuleFormSeed({ entry });
    setRuleFormOpen(true);
  }

  // ---- Render ----

  return (
    <div className="responder-availability" style={{ userSelect: dragging ? 'none' : 'auto' }}>
      <header className="ra-header" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>When am I available?</h2>
        <div className="ra-actions" style={{ gap: 6 }}>
          <button
            className="btn-ghost-danger"
            onClick={() => setSickModalOpen(true)}
            title="Mark yourself out AND ask teammates to cover"
          >
            Mark sick (ask for cover)
          </button>
          <button
            className="btn-ghost"
            onClick={cleanDuplicates}
            disabled={bulkBusy}
            title="Detect + remove redundant overlapping entries"
          >
            {bulkBusy ? 'Cleaning...' : 'Clean up duplicates'}
          </button>
        </div>
      </header>

      <ModelBanner availabilityModel={availabilityModel} />

      <div className="ra-tip" style={{
        marginTop: 8, padding: '8px 12px', borderRadius: 6,
        background: '#f0f9ff', color: '#075985', fontSize: 13,
      }}>
        <strong>Click a day</strong> to set its status. <strong>Click and drag</strong> across
        days to update a range at once. You can set availability up to <strong>28 days ahead</strong>.
      </div>

      {error && <div className="error-banner" style={{ marginTop: 8 }}>{error}</div>}
      {cleanupMsg && (
        <div className="ra-banner" style={{
          marginTop: 8, background: '#dcfce7', borderLeft: '4px solid #16a34a', color: '#166534',
          padding: '8px 12px',
        }}>
          {cleanupMsg}
        </div>
      )}

      {/* === Calendar window (CAL-WINDOW: fixed 4 days back → 28 days forward) === */}
      <section className="ra-section" style={{ marginTop: 12 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          marginBottom: 10, flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{windowLabel}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            Next 28 days · last 4 days shown for context
          </div>
        </div>

        <div className="ra-day-headers">
          {DAY_NAMES.map((d) => (
            <div key={d} className="ra-day-header">{d}</div>
          ))}
        </div>

        {/* Big calendar grid */}
        <div
          className="ra-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 6,
          }}
        >
          {visibleRows.flatMap((row) => row.map((cell) => (
            <BigCalendarCell
              key={cell.ymd}
              cell={cell}
              selected={selection.has(cell.ymd)}
              onPointerDown={() => { if (!cell.disabled) onPointerDownCell(cell.ymd); }}
              onPointerEnter={() => { if (!cell.disabled) onPointerEnterCell(cell.ymd); }}
              onClick={() => {
                if (cell.disabled) return; // past + beyond-window cells are non-interactive
                if (!dragging && selection.size === 0) {
                  setSelection(new Set([cell.ymd]));
                  setPopoverOpen(true);
                }
              }}
            />
          )))}
        </div>

        <div className="ra-legend" style={{ marginTop: 10 }}>
          <span className="ra-legend-item"><span className="ra-dot ra-dot-available" /> Available</span>
          <span className="ra-legend-item"><span className="ra-dot ra-dot-unavailable" /> Unavailable</span>
          <span className="ra-legend-item"><span className="ra-dot ra-dot-off" /> Off</span>
          {availabilityModel === 'opt-out' && (
            <span className="ra-legend-note">
              (your team is opt-out — "Off" days count as available unless you mark them otherwise)
            </span>
          )}
        </div>
      </section>

      {/* === Advanced (collapsible) === */}
      <section className="ra-section" style={{ marginTop: 12 }}>
        <button
          onClick={() => setAdvancedOpen((v) => !v)}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 14, color: 'var(--muted)', padding: '6px 0',
          }}
        >
          {advancedOpen ? '▼' : '▶'} Advanced — manage rules ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
        </button>

        {advancedOpen && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <button
                className="btn-ghost"
                onClick={() => { setRuleFormSeed(null); setRuleFormOpen(true); }}
                style={{ fontSize: 13, padding: '6px 12px' }}
              >
                + Add custom rule (form)
              </button>
            </div>

            {loadingEntries ? (
              <div className="placeholder">Loading...</div>
            ) : entries.length === 0 ? (
              <div className="placeholder">No entries yet.</div>
            ) : (
              <>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '8px 12px', marginBottom: 8,
                  background: '#f8fafc', borderRadius: 6, fontSize: 13,
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedAdvIds.size === entries.length && entries.length > 0}
                      ref={(el) => { if (el) el.indeterminate = selectedAdvIds.size > 0 && selectedAdvIds.size < entries.length; }}
                      onChange={(e) => (e.target.checked ? selectAllAdv() : clearAdvSelection())}
                      disabled={bulkBusy}
                    />
                    <span>
                      {selectedAdvIds.size === 0
                        ? `Select all (${entries.length})`
                        : `${selectedAdvIds.size} of ${entries.length} selected`}
                    </span>
                  </label>
                  <div style={{ flex: 1 }} />
                  {selectedAdvIds.size > 0 && (
                    <>
                      <button className="btn-ghost" onClick={clearAdvSelection}
                        disabled={bulkBusy} style={{ fontSize: 13, padding: '4px 10px' }}>
                        Clear
                      </button>
                      <button onClick={deleteSelectedAdv} disabled={bulkBusy}
                        style={{
                          fontSize: 13, padding: '4px 12px',
                          background: '#dc2626', color: 'white', border: 'none',
                          borderRadius: 6, cursor: bulkBusy ? 'wait' : 'pointer', fontWeight: 600,
                        }}>
                        {bulkBusy ? 'Deleting...' : `Delete ${selectedAdvIds.size} selected`}
                      </button>
                    </>
                  )}
                </div>

                <ul className="ra-entry-list">
                  {entries.map((entry) => (
                    <li key={entry.id} className={`ra-entry ra-entry-${entry.status}`}>
                      <input
                        type="checkbox"
                        checked={selectedAdvIds.has(entry.id)}
                        onChange={() => toggleAdvSelected(entry.id)}
                        disabled={bulkBusy}
                        style={{ marginRight: 10, cursor: 'pointer' }}
                      />
                      <div className="ra-entry-text">
                        {describeEntry(entry, tz)}
                        {entry.type !== 'one-off' && (
                          <span style={{
                            marginLeft: 8, fontSize: 11, fontWeight: 600,
                            padding: '1px 6px', borderRadius: 999,
                            background: '#fef3c7', color: '#92400e',
                          }}>recurring</span>
                        )}
                      </div>
                      <div className="ra-entry-actions">
                        <button onClick={() => openRuleFormForEdit(entry)} disabled={bulkBusy}>Edit</button>
                        <button className="link-danger" onClick={() => removeEntry(entry.id)} disabled={bulkBusy}>Delete</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </section>

      {/* === Popover for the selected day(s) === */}
      {popoverOpen && selection.size > 0 && (
        <ApplyStatusModal
          selection={selection}
          entries={entries}
          timezone={tz}
          onCancel={() => { setPopoverOpen(false); setSelection(new Set()); }}
          onSave={saveStatusForSelection}
        />
      )}

      {sickModalOpen && (
        <SickModal
          timezone={tz}
          onClose={() => setSickModalOpen(false)}
          onError={(msg) => setError(msg)}
        />
      )}

      {ruleFormOpen && (
        <AvailabilityModal
          seed={ruleFormSeed}
          timezone={tz}
          onClose={() => setRuleFormOpen(false)}
          onError={(msg) => setError(msg)}
        />
      )}
    </div>
  );
}

// ============================================================================
// BigCalendarCell
// ============================================================================

function BigCalendarCell({ cell, selected, onPointerDown, onPointerEnter, onClick }) {
  const day = cell.date.getDate();
  const today = new Date();
  const isToday =
    cell.date.getFullYear() === today.getFullYear() &&
    cell.date.getMonth() === today.getMonth() &&
    cell.date.getDate() === today.getDate();

  let bg = '#f9fafb';        // off
  let border = '#e5e7eb';
  let textColor = cell.inMonth ? '#1f2937' : '#9ca3af';

  if (cell.status === 'available') {
    bg = '#dcfce7';
    border = '#16a34a';
  } else if (cell.status === 'unavailable') {
    bg = '#fee2e2';
    border = '#dc2626';
  } else if (cell.implicitlyAvailable) {
    bg = '#f0fdf4';
    border = '#bbf7d0';
  }

  if (!cell.inMonth) {
    bg = '#fafafa';
    border = '#f3f4f6';
  }
  if (selected) {
    border = '#2563eb';
    bg = '#dbeafe';
    textColor = '#1e3a8a';
  }

  // CAL-FUTURE 2026-05-17: past cells are visually faded + non-interactive.
  // The button is still rendered (grid alignment matters) but stripped of
  // interactivity. Today and future stay clickable.
  const isPast = !!cell.isPast;
  const disabled = !!cell.disabled;
  let cellOpacity = 1;
  if (isPast) cellOpacity = 0.35;
  else if (!cell.inMonth) cellOpacity = 0.55;

  return (
    <button
      onPointerDown={(e) => { if (!disabled) { e.preventDefault(); onPointerDown(); } }}
      onPointerEnter={!disabled ? onPointerEnter : undefined}
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
      title={disabled ? `${cell.ymd}${isPast ? ' (past — not editable)' : ' (beyond the 28-day window)'}` : cell.ymd}
      style={{
        background: bg,
        border: `2px solid ${border}`,
        borderRadius: 8,
        minHeight: 84,
        padding: 6,
        cursor: isPast ? 'default' : 'pointer',
        textAlign: 'left',
        fontSize: 14,
        color: textColor,
        position: 'relative',
        outline: selected ? '2px solid #2563eb' : 'none',
        outlineOffset: -4,
        opacity: cellOpacity,
        touchAction: 'none',
        filter: isPast ? 'grayscale(0.6)' : 'none',
      }}
    >
      <div style={{ fontWeight: isToday ? 700 : 500, fontSize: 16 }}>
        {day}
        {isToday && (
          <span style={{
            marginLeft: 4, fontSize: 10, fontWeight: 700,
            background: '#2563eb', color: 'white',
            padding: '1px 5px', borderRadius: 4, verticalAlign: 'middle',
          }}>TODAY</span>
        )}
      </div>
    </button>
  );
}

// ============================================================================
// ModelBanner
// ============================================================================

function ModelBanner({ availabilityModel }) {
  if (availabilityModel === 'opt-in') {
    return (
      <div className="ra-banner">
        <strong>Opt-in mode:</strong> your team treats you as off-call by default.
        Click days to add availability.
      </div>
    );
  }
  return (
    <div className="ra-banner ra-banner-soft">
      <strong>Opt-out mode:</strong> your team treats you as on-call by default.
      Click days to mark yourself unavailable for sick days, vacation, etc.
    </div>
  );
}

// ============================================================================
// ApplyStatusModal — the "what to do with the selected day(s)" popover
// ============================================================================

function ApplyStatusModal({ selection, entries, timezone, onCancel, onSave }) {
  const sortedYmds = useMemo(() => [...selection].sort(), [selection]);
  const firstYmd = sortedYmds[0];
  const lastYmd = sortedYmds[sortedYmds.length - 1];
  const count = sortedYmds.length;

  // All selected days share the same weekday? If yes, weekly + every-other are
  // meaningful. Otherwise only "just these days" makes sense.
  const uniqueDows = new Set(sortedYmds.map((y) => ymdToDate(y).getDay()));
  const canRecur = uniqueDows.size === 1;
  const recurWeekday = canRecur ? DAY_NAMES[[...uniqueDows][0]] : null;

  // What entries currently cover the first selected day? Useful context.
  const covering = entries.filter((e) => entryCoversDate(e, firstYmd, timezone));

  const [status, setStatus] = useState('available');
  const [applyTo, setApplyTo] = useState('just-these');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleSave() {
    setBusy(true);
    try {
      await onSave({ status, applyTo, reason });
    } finally {
      setBusy(false);
    }
  }

  const rangeLabel = count === 1
    ? formatFriendlyDate(ymdToDate(firstYmd))
    : `${formatFriendlyDate(ymdToDate(firstYmd))} → ${formatFriendlyDate(ymdToDate(lastYmd))} (${count} day${count === 1 ? '' : 's'})`;

  return (
    <div
      className="ra-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="ra-modal" style={{ maxWidth: 520 }}>
        <h3 style={{ marginBottom: 4 }}>{rangeLabel}</h3>

        {covering.length > 0 && (
          <div style={{
            marginTop: 6, padding: 10, background: '#f8fafc', borderRadius: 6,
            fontSize: 13, color: 'var(--muted)',
          }}>
            <strong>Currently:</strong>{' '}
            {covering.map((e, i) => (
              <span key={e.id}>
                {i > 0 ? '; ' : ''}{describeEntry(e, timezone)}
              </span>
            ))}
          </div>
        )}

        <div className="ra-field" style={{ marginTop: 12 }}>
          <label>Set status</label>
          <div className="ra-pills">
            <button
              type="button"
              className={`ra-pill ${status === 'available' ? 'is-active' : ''}`}
              onClick={() => setStatus('available')}
            >Available</button>
            <button
              type="button"
              className={`ra-pill ${status === 'unavailable' ? 'is-active' : ''}`}
              onClick={() => setStatus('unavailable')}
            >Unavailable</button>
          </div>
        </div>

        <div className="ra-field">
          <label>Apply to</label>
          <div className="ra-pills" style={{ flexWrap: 'wrap' }}>
            <button
              type="button"
              className={`ra-pill ${applyTo === 'just-these' ? 'is-active' : ''}`}
              onClick={() => setApplyTo('just-these')}
            >Just {count === 1 ? 'this day' : `these ${count} days`}</button>
            <button
              type="button"
              className={`ra-pill ${applyTo === 'weekly' ? 'is-active' : ''}`}
              onClick={() => canRecur && setApplyTo('weekly')}
              disabled={!canRecur}
              title={canRecur ? '' : 'All selected days must share the same weekday'}
              style={{ opacity: canRecur ? 1 : 0.4 }}
            >Every {recurWeekday || '...'}</button>
            <button
              type="button"
              className={`ra-pill ${applyTo === 'every-other' ? 'is-active' : ''}`}
              onClick={() => canRecur && setApplyTo('every-other')}
              disabled={!canRecur}
              title={canRecur ? '' : 'All selected days must share the same weekday'}
              style={{ opacity: canRecur ? 1 : 0.4 }}
            >Every other {recurWeekday || '...'}</button>
          </div>
        </div>

        <div className="ra-field">
          <label htmlFor="apply-reason">Reason (optional)</label>
          <input
            id="apply-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="ra-input"
            maxLength={200}
            placeholder="e.g. school pickup, holiday, on call"
            disabled={busy}
          />
        </div>

        <div className="ra-modal-actions">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={busy}
            style={{ background: status === 'unavailable' ? '#dc2626' : '#10b981' }}
          >
            {busy ? 'Saving...' : `Set ${status}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// AvailabilityModal - kept for the "Add custom rule" path in the Advanced panel
// ============================================================================

function AvailabilityModal({ seed, timezone, onClose, onError }) {
  const editing = !!seed?.entry;
  const seededEntry = seed?.entry || {};
  const editingRecurring = editing && seededEntry.type && seededEntry.type !== 'one-off';

  const [status, setStatus] = useState(seededEntry.status || 'available');
  const [type, setType] = useState(seededEntry.type || 'one-off');
  const [startDate, setStartDate] = useState(
    seededEntry.startDate
      ? ymdInTimezone(toJsDate(seededEntry.startDate), timezone)
      : todayYMD(timezone),
  );
  const [endDate, setEndDate] = useState(
    seededEntry.endDate ? ymdInTimezone(toJsDate(seededEntry.endDate), timezone) : '',
  );
  const [daysOfWeek, setDaysOfWeek] = useState(
    Array.isArray(seededEntry.daysOfWeek) ? [...seededEntry.daysOfWeek] : [],
  );
  const [weekInterval, setWeekInterval] = useState(seededEntry.weekInterval || 2);
  const [reason, setReason] = useState(seededEntry.reason || '');

  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);

  function toggleDay(d) {
    setDaysOfWeek((current) => {
      if (current.includes(d)) return current.filter((x) => x !== d);
      return [...current, d].sort((a, b) => a - b);
    });
  }

  async function save() {
    setLocalError(null);
    setBusy(true);
    try {
      const payload = {
        status,
        type,
        startDate: ymdToMidnightISO(startDate),
        endDate: endDate ? ymdToMidnightISO(endDate) : null,
        daysOfWeek: (type === 'weekly' || type === 'every-n-weeks') ? daysOfWeek : null,
        weekInterval: type === 'every-n-weeks' ? Number(weekInterval) : null,
        reason: reason.trim() || null,
      };
      if (editing) {
        await httpsCallable(functions, 'updateAvailability')({ id: seed.entry.id, patch: payload });
      } else {
        await httpsCallable(functions, 'addAvailability')({ entry: payload });
      }
      onClose();
    } catch (err) {
      const msg = err?.message || String(err);
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal">
        <h3>{editing ? 'Edit rule' : 'Add custom rule'}</h3>

        {editingRecurring && (
          <div className="ra-banner" style={{
            background: '#fef3c7', borderLeft: '4px solid #f59e0b', color: '#92400e',
            marginBottom: 12, padding: '10px 12px',
          }}>
            <strong>Editing a recurring rule.</strong> Saving changes ALL instances.
          </div>
        )}

        <div className="ra-field">
          <label>Status</label>
          <div className="ra-pills">
            <button type="button" className={`ra-pill ${status === 'available' ? 'is-active' : ''}`} onClick={() => setStatus('available')}>Available</button>
            <button type="button" className={`ra-pill ${status === 'unavailable' ? 'is-active' : ''}`} onClick={() => setStatus('unavailable')}>Unavailable</button>
          </div>
        </div>

        <div className="ra-field">
          <label>Repeats</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className="ra-input">
            <option value="one-off">Doesn't repeat (one-off)</option>
            <option value="weekly">Weekly</option>
            <option value="every-n-weeks">Every N weeks</option>
          </select>
        </div>

        <div className="ra-field-row">
          <div className="ra-field">
            <label>Start date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="ra-input" />
          </div>
          <div className="ra-field">
            <label>End date (optional)</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="ra-input" />
          </div>
        </div>

        {(type === 'weekly' || type === 'every-n-weeks') && (
          <div className="ra-field">
            <label>Days of week</label>
            <div className="ra-dow">
              {DAY_NAMES.map((name, i) => (
                <button key={name} type="button"
                  className={`ra-dow-btn ${daysOfWeek.includes(i) ? 'is-active' : ''}`}
                  onClick={() => toggleDay(i)}>{name}</button>
              ))}
            </div>
          </div>
        )}

        {type === 'every-n-weeks' && (
          <div className="ra-field">
            <label>Cadence</label>
            <select value={weekInterval}
              onChange={(e) => setWeekInterval(Number(e.target.value))}
              className="ra-input">
              <option value={2}>Every 2 weeks</option>
              <option value={3}>Every 3 weeks</option>
              <option value={4}>Every 4 weeks</option>
            </select>
          </div>
        )}

        <div className="ra-field">
          <label>Reason (optional)</label>
          <input type="text" value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="ra-input" maxLength={200} />
        </div>

        {localError && <div className="error-banner">{localError}</div>}

        <div className="ra-modal-actions">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving...' : (editing ? 'Save changes' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SickModal
// ============================================================================

function SickModal({ timezone, onClose, onError }) {
  const [affectedDate, setAffectedDate] = useState(todayYMD(timezone));
  const [reason, setReason] = useState('sick');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [success, setSuccess] = useState(null);

  async function send() {
    setLocalError(null);
    setBusy(true);
    try {
      const res = await httpsCallable(functions, 'requestCover')({
        affectedDate: ymdToMidnightISO(affectedDate),
        reason: reason.trim() || null,
      });
      const candidateCount = res?.data?.candidateCount ?? 0;
      setSuccess(
        candidateCount > 0
          ? `Marked unavailable. ${candidateCount} teammate${candidateCount === 1 ? '' : 's'} notified.`
          : `Marked unavailable. No teammates available — admin can intervene from the coverage page.`,
      );
    } catch (err) {
      const msg = err?.message || String(err);
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal">
        <h3>Mark sick &amp; ask for cover</h3>
        <p className="settings-help" style={{ marginTop: -8 }}>
          You'll be marked unavailable for this date and teammates will get a push asking
          if they can pick up the shift. First to volunteer claims it.
        </p>
        <div className="ra-field">
          <label>Date</label>
          <input type="date" value={affectedDate}
            onChange={(e) => setAffectedDate(e.target.value)}
            className="ra-input" disabled={busy || !!success} />
        </div>
        <div className="ra-field">
          <label>Reason (optional)</label>
          <input type="text" value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="ra-input" maxLength={200}
            disabled={busy || !!success} />
        </div>
        {localError && <div className="error-banner">{localError}</div>}
        {success && (
          <div className="ra-banner" style={{ borderLeftColor: '#10b981' }}>
            <strong>OK</strong> {success}
          </div>
        )}
        <div className="ra-modal-actions">
          {!success ? (
            <>
              <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn-primary" onClick={send} disabled={busy}>
                {busy ? 'Asking...' : 'Mark sick & ask for cover'}
              </button>
            </>
          ) : (
            <button className="btn-primary" onClick={onClose}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================


// CAL-SHADOW 2026-05-17: shadow-detection helpers used by cleanDuplicates.
function createdAtMs(entry) {
  const c = entry.createdAt;
  if (!c) return 0;
  if (typeof c.toMillis === 'function') return c.toMillis();
  if (typeof c.seconds === 'number') return c.seconds * 1000;
  if (c instanceof Date) return c.getTime();
  return 0;
}

function datesForOneOffEntry(e, tz) {
  if (e.type !== 'one-off') return [];
  const start = ymdInTimezone(toJsDate(e.startDate), tz);
  if (!start) return [];
  const end = e.endDate ? ymdInTimezone(toJsDate(e.endDate), tz) : start;
  const result = [];
  let cur = ymdToDate(start);
  const endD = ymdToDate(end);
  let guard = 0;
  while (cur.getTime() <= endD.getTime() && guard < 366) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    result.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
    guard++;
  }
  return result;
}

function ymdToDate(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function todayYMD(timezone) {
  return ymdInTimezone(new Date(), timezone);
}

function ymdToMidnightISO(ymd) {
  return new Date(`${ymd}T12:00:00.000Z`).toISOString();
}

function formatFriendlyDate(date, timezone) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(date);
  } catch {
    return date.toDateString();
  }
}

function groupContiguous(sortedYmds) {
  if (sortedYmds.length === 0) return [];
  const groups = [[sortedYmds[0]]];
  for (let i = 1; i < sortedYmds.length; i++) {
    const prev = ymdToDate(sortedYmds[i - 1]);
    const cur = ymdToDate(sortedYmds[i]);
    const dayDiff = Math.round((cur - prev) / (24 * 60 * 60 * 1000));
    if (dayDiff === 1) {
      groups[groups.length - 1].push(sortedYmds[i]);
    } else {
      groups.push([sortedYmds[i]]);
    }
  }
  return groups;
}
